/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import * as RadixUiReactToggleGroup from "@radix-ui/react-toggle-group";
import type { ComponentProps } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { colors, radius, spacing } from "../../../../../global/emotion";
import { Text } from "../Text/Text.emotion";

const isActiveStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  "::after": {
    backgroundColor: theme.semanticColors.primary,
    bottom: 0,
    content: '""',
    height: 2,
    left: 0,
    marginLeft: spacing.m,
    marginRight: spacing.m,
    position: "absolute",
    width: `calc(100% - calc(${spacing.m} * 2))`,
  },
});
const badgeStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.s,
  paddingBottom: spacing.t,
  paddingLeft: spacing.xxs,
  paddingRight: spacing.xxs,
  paddingTop: spacing.t,
});
const radixUiReactToggleGroupItemStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: colors.transparent,
  ":hover": { backgroundColor: theme.semanticColors.neutralsLowest },
  ":active": { backgroundColor: theme.semanticColors.neutralsLowest },
  border: "none",
  borderTopLeftRadius: radius.s,
  borderTopRightRadius: radius.s,
  color: theme.semanticColors.neutralsLow,
  cursor: "pointer",
  display: "flex",
  gap: spacing.xxs,
  marginBottom: -2,
  padding: spacing.m,
  position: "relative",
  transition: "background-color 0.25s ease-in-out",
});

type TabsItemProps = ComponentProps<typeof RadixUiReactToggleGroup.Item> & {
  badge?: number | string;
  parentState: string;
};

export const TabsItem = ({
  badge,
  children,
  parentState,
  value,
  ...props
}: TabsItemProps) => {
  const isActive = parentState === value;

  return (
    <RadixUiReactToggleGroup.Item
      value={value}
      css={[radixUiReactToggleGroupItemStyle, isActive && isActiveStyle]}
      {...props}
    >
      <Text size="m" type="title">
        {children}
      </Text>
      {badge ? (
        <div css={badgeStyle}>
          <Text size="xs" type="title">
            {badge}
          </Text>
        </div>
      ) : null}
    </RadixUiReactToggleGroup.Item>
  );
};
